// inv/src/components/lists/SingleList.jsx
import { useEffect, useState, useCallback } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useInitialData } from '../data/DataContext'
import { useTranslation } from 'react-i18next';
import { getArticleBySlug, getOtherArticles, getInvestors } from './ListService';
import { Table } from '../ui/table';
import HoverHint from '../ui/hoverHint';
import RenderContent from './RenderContent';
import Breadcrumbs from '../../seo/breadcrumbs';
import SeoHead from '../../seo/seoHead'
import { Globe } from 'lucide-react';
import FacebookIcon from '../../icons/facebook.svg';
import InstagramIcon from '../../icons/instagram.svg';
import LinkedinIcon from '../../icons/linkedin.svg';
import XIcon from '../../icons/x.svg';
import InvestorsStats from './InvestorsStats';
import {
  buildInvestorProfilePath,
  hasField,
  hasValue,
  INVESTOR_CONTACT_HINT,
  INVESTOR_SOCIAL_HINT,
} from '../investor-display/helpers';

const SingleList = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const initialData = useInitialData();
  const {
    article: initialArticle = null,
    related: initialRelated = [],
    investors: initialInvestors = [],
    totalInvestors: initialTotal = 0,
    seo = {}
  } = initialData;
  const [article, setArticle] = useState(initialArticle);
  const [otherArticles, setOtherArticles] = useState(initialRelated || []);

  const [investors, setInvestors] = useState(initialInvestors || []);
  const [totalInvestors, setTotalInvestors] = useState(initialTotal || 0);
  const [itemsToShow, setItemsToShow] = useState(10);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const data = await getArticleBySlug(slug);
        setArticle(data);
        setItemsToShow(data.items_to_show || 10);
      } catch (error) {
        console.error("Error fetching article:", error);
      }
    };
    fetchArticle();
  }, [slug]);

  useEffect(() => {
    const fetchOther = async () => {
      try {
        const data = await getOtherArticles(slug, 5);
        setOtherArticles(data.lists || []);
      } catch (error) {
        console.error("Error fetching other articles:", error);
      }
    };
    if (article) {
      fetchOther();
    }
  }, [article, slug]);

  const fetchInvestorsByParams = useCallback(
    async (country, industries, types) => {
      try {
        const page = 1;
        const limit = itemsToShow;
        const { data, total } = await getInvestors(country, industries, page, limit, types);

        setInvestors(data);
        setTotalInvestors(total);
      } catch (error) {
        console.error("Error fetching investors:", error);
      }
    },
    [itemsToShow]
  );

  useEffect(() => {
    if (article?.country) {
      fetchInvestorsByParams(article.country, article.industries || '', article.types || []);
    }
  }, [article, fetchInvestorsByParams]);

  if (!article) return <div>Loading...</div>;

  const generateInvestorLink = (investor) => {
    return buildInvestorProfilePath({ investor, language: i18n.language });
  };

  const baseColumns = [
    { header: "Name", accessor: "name", className: "font-medium" },
    { header: "Short Info", accessor: "short_info", className: "min-w-[300px]" },
    { header: "Email", accessor: "email" },
    { header: "Phone", accessor: "phone" },
    { header: "Website", accessor: "website_url" },
    { header: "Socials", accessor: "socials" },
    { header: "Location", accessor: "location" },
  ];

  const visibleColumns = baseColumns.filter((col) => {
    switch (col.accessor) {
      case "website_url":
        return investors.some((inv) => hasField(inv, "website_url"));
      case "email":
        return investors.some((inv) => hasField(inv, "email"));
      case "phone":
        return investors.some((inv) => hasField(inv, "phone"));
      case "socials":
        return investors.some(
          (inv) =>
            hasField(inv, "fb_url") ||
            hasField(inv, "inst_url") ||
            hasField(inv, "linkedin_url") ||
            hasField(inv, "x_url")
        );
      default:
        return investors.some((inv) => hasValue(inv[col.accessor]));
    }
  });

  // SEO Data:

  return (
    <>
      <SeoHead
        lang={i18n.language}
        title={seo.title}
        description={seo.description}
        canonicalUrl={seo.canonicalUrl}
        ogTitle={seo.ogTitle}
        ogDescription={seo.ogDescription}
        ogImage={seo.ogImage}
        ogUrl={seo.ogUrl}
        ogType={seo.ogType}
        twitterCard={seo.twitterCard}
        alternates={seo.alternates}
        jsonLd={seo.jsonLd}
      />
      <div className="container px-12 md:px-12 lg:px-[20%] my-20">
        <Breadcrumbs
          mapSegments={{
            lists: t('menu.lists'),
          }}
          pageTitle={article.title}
        />
        {!article.title ? (
          <div className="animate-pulse h-8 w-64 bg-gray-300 rounded"></div>
        ) : (
          <h1 className="mt-8 !text-3xl sm:!text-4xl leading-10 font-bold">{article.title}</h1>
        )}      <p className="mt-6 mb-6">
          {article.publish_date ? new Date(article.publish_date).toLocaleDateString() : "Unknown date"}
        </p>

        {article.short_description && <p className="text-base">{article.short_description}</p>}
        <InvestorsStats
          total={totalInvestors}
          industry={article.industries}
          country={article.country}
        />

        <Table
          columns={visibleColumns}
          data={investors.slice(0, itemsToShow)}
          className="mt-6"
          renderCell={(value, column, row) => {
            if (column.accessor === "name") {
              return (
                <RouterLink to={generateInvestorLink(row)} className="text-blue-600 underline font-medium">
                  {value}
                </RouterLink>
              );
            }
            if (column.accessor === "website_url") {
              return hasField(row, "website_url") ? (
                <HoverHint message={INVESTOR_SOCIAL_HINT}>
                  <span className="inline-flex items-center">
                    <Globe size={18} className="mr-1" aria-label="Website (members only)" />
                  </span>
                </HoverHint>
              ) : "-";
            }
            if (column.accessor === "socials") {
              const socials = [
                { key: "fb_url", icon: FacebookIcon, alt: "Facebook" },
                { key: "inst_url", icon: InstagramIcon, alt: "Instagram" },
                { key: "linkedin_url", icon: LinkedinIcon, alt: "LinkedIn" },
                { key: "x_url", icon: XIcon, alt: "X (Twitter)" },
              ].filter((s) => hasField(row, s.key));

              return socials.length ? (
                <div className="flex space-x-2">
                  {socials.map((s, idx) => (
                    <HoverHint key={idx} message={INVESTOR_SOCIAL_HINT}>
                      <img src={s.icon} alt={s.alt} className="w-5 h-5" />
                    </HoverHint>
                  ))}
                </div>
              ) : "-";
            }
            if (column.accessor === "email") {
              return hasField(row, "email") ? (
                <HoverHint message={INVESTOR_CONTACT_HINT}>
                  <span className="inline-flex items-center">📧</span>
                </HoverHint>
              ) : "-";
            }

            if (column.accessor === "phone") {
              return hasField(row, "phone") ? (
                <HoverHint message={INVESTOR_CONTACT_HINT}>
                  <span className="inline-flex items-center">📞</span>
                </HoverHint>
              ) : "-";
            }
            return value || "-";
          }}
        />

        <div style={{ marginTop: "100px", marginBottom: "80px" }}>
          <RenderContent contentObject={article.content} />
        </div>

        {otherArticles.length > 0 && (
          <div style={{ marginTop: "0px" }}>
            <h2 style={{ fontSize: "1.5rem", marginBottom: "40px" }}>{t('other.t5')}</h2>
            {otherArticles.map((other) => (
              <div
                key={other._id}
                style={{
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                  marginBottom: "20px",
                  padding: "15px",
                }}
              >
                <RouterLink
                  to={`${i18n.language === 'en' ? '' : `/${i18n.language}`}/lists/${other.url_slug}`}
                  style={{ textDecoration: "none", color: "inherit" }}>
                  <h3 style={{ margin: "0 0 10px 0" }}>{other.title}</h3>
                  <p style={{ color: "#666", margin: "0 0 10px 0" }}>
                    {new Date(other.publish_date).toLocaleDateString()}
                  </p>
                  <p style={{ lineHeight: "1.6" }}>{other.short_description}</p>
                </RouterLink>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default SingleList;
